import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showAboutMenu, setShowAboutMenu] = useState(false);
  const [showCustomerSupportMenu, setShowCustomerSupportMenu] = useState(false);
  const [showBusinessMenu, setShowBusinessMenu] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [showSubwayMap, setShowSubwayMap] = useState(false);
  const [subwayMapScale, setSubwayMapScale] = useState(1);
  const [subwayMapPosition, setSubwayMapPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [productSlideOffset, setProductSlideOffset] = useState(0);
  const [equipmentSlideOffset, setEquipmentSlideOffset] = useState(0);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  // Add refs to prevent cleanup issues
  const heroSliderRef = useRef<NodeJS.Timeout | null>(null);
  const productSliderRef = useRef<NodeJS.Timeout | null>(null);
  const equipmentSliderRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Equipment images for hero slider
  const heroImages = [
    'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/c2de89013e90697dadbddf3f11636022.png',
    'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/0d2e1176096a88a27e6664e862302f4a.png',
    'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/0a09acef1c69e5e2db4b92aabb68752e.png',
    'https://public.readdy.ai/ai/img_res/633f323f6a2847b180bec1e06d221821.jpg'
  ];

  // Product images for bottom slider
  const productImages = [
    { name: t('hero.product1'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/e00a3d63324a0df941d023242cf9a486.png' },
    { name: t('hero.product2'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/95981adf7576dca59d06bdedc71083dc.png' },
    { name: t('hero.product5'), image: 'https://readdy.ai/api/search-image?query=Soft%20baby%20pillow%20with%20gentle%20support%2C%20infant%20sleeping%20accessory%2C%20pastel%20colored%20fabric%2C%20professional%20product%20photography%2C%20clean%20white%20background%2C%20comfortable%20nursery%20item&width=400&height=400&seq=product-baby-pillow-005&orientation=squarish' },
      { name: t('hero.product7'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/cfd7523e7b56cd48c070e2aaeed1c701.png' },
    { name: t('hero.product9'), image: 'https://readdy.ai/api/search-image?query=Modern%20bicycle%20helmet%20with%20aerodynamic%20design%20and%20safety%20features%2C%20cycling%20protective%20gear%2C%20professional%20product%20photography%2C%20clean%20white%20background%2C%20sports%20equipment&width=400&height=400&seq=product-bike-helmet-009&orientation=squarish' },
      { name: t('hero.product10'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/27c354a0d71b7233ba9a871277550c13.png' },
      { name: t('hero.product11'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/1dcabd3cce0fca6eec8ba9979b5175a2.png' },
      { name: t('hero.product12'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/9d2386ca31dfff523d90ad2a34f949e0.png' },
      { name: t('hero.product13'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/9f7af8c5ad62584436bf57b060aec64e.png' },
      { name: t('hero.product14'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/cd595b719b743688c18d11f1f4d080a4.png' },
      { name: t('hero.product15'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/64b26ab5220be4b2e35a8bbeb6593041.png' },
      { name: t('hero.product16'), image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/8e8dbd5329022fb6b0016450382cedd2.png' }
  ];

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const [languages, setLanguages] = useState([
    { code: 'zh', name: '中文', flag: 'https://flagcdn.com/w40/cn.png', icon: 'ri-flag-fill', color: 'text-red-600' },
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/us.png', icon: 'ri-flag-fill', color: 'text-blue-600' },
    { code: 'ko', name: '한국어', flag: 'https://flagcdn.com/w40/kr.png', icon: 'ri-flag-fill', color: 'text-red-600' },
    { code: 'ja', name: '日本語', flag: 'https://flagcdn.com/w40/jp.png', icon: 'ri-flag-fill', color: 'text-red-600' }
  ]);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setShowLangMenu(false);
  };

  useEffect(() => {
    isMountedRef.current = true;

    const handleScroll = () => {
      if (!isMountedRef.current) return;
      setIsScrolled(window.scrollY > 50);
      
      const sections = ['home', 'about', 'business', 'services', 'certifications', 'equipment-section', 'resources', 'contact', 'location'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current && isMountedRef.current) {
        // Map equipment-section to equipment for active state
        setActiveSection(current === 'equipment-section' ? 'equipment' : current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Hero slider auto-play
  useEffect(() => {
    if (heroSliderRef.current) {
      clearInterval(heroSliderRef.current);
    }

    heroSliderRef.current = setInterval(() => {
      if (isMountedRef.current) {
        setCurrentSlide((prev) => (prev + 1) % 4);
      }
    }, 5000);

    return () => {
      if (heroSliderRef.current) {
        clearInterval(heroSliderRef.current);
        heroSliderRef.current = null;
      }
    };
  }, []);

  // Product slider auto-play
  useEffect(() => {
    if (productSliderRef.current) {
      clearInterval(productSliderRef.current);
    }

    productSliderRef.current = setInterval(() => {
      if (isMountedRef.current) {
        setProductSlideOffset((prev) => (prev + 1) % productImages.length);
      }
    }, 3000);

    return () => {
      if (productSliderRef.current) {
        clearInterval(productSliderRef.current);
        productSliderRef.current = null;
      }
    };
  }, [productImages.length]);

  // Equipment slider auto-play
  useEffect(() => {
    const interval = setInterval(() => {
      setEquipmentSlideOffset((prev) => (prev + 1) % 21);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const scrollToEquipment = () => {
    const equipmentSection = document.getElementById('equipment-section');
    if (equipmentSection) {
      equipmentSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubwayMapWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setSubwayMapScale(prev => Math.min(Math.max(0.5, prev + delta), 3));
  };

  const handleSubwayMapMouseDown = (e: React.MouseEvent) => {
    if (subwayMapScale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - subwayMapPosition.x, y: e.clientY - subwayMapPosition.y });
    }
  };

  const handleSubwayMapMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setSubwayMapPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleSubwayMapMouseUp = () => {
    setIsDragging(false);
  };

  const handleSubwayMapDoubleClick = () => {
    if (subwayMapScale === 1) {
      setSubwayMapScale(2);
    } else {
      setSubwayMapScale(1);
      setSubwayMapPosition({ x: 0, y: 0 });
    }
  };

  const resetSubwayMap = () => {
    setSubwayMapScale(1);
    setSubwayMapPosition({ x: 0, y: 0 });
  };

  const closeSubwayMap = () => {
    setShowSubwayMap(false);
    resetSubwayMap();
  };

  const goToEquipmentSlide = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setEquipmentSlideOffset((prev) => (prev - 1 + 21) % 21);
    } else {
      setEquipmentSlideOffset((prev) => (prev + 1) % 21);
    }
  };

  const slides = [
    {
      title: t('hero.slide1Title'),
      description: '',
      image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/c2de89013e90697dadbddf3f11636022.png',
      icon: 'ri-test-tube-line',
      mainTitle: t('hero.slide1MainTitle'),
      points: [
        { label: t('hero.slide1Point1'), text: t('hero.slide1Point1Text') },
        { label: t('hero.slide1Point2'), text: t('hero.slide1Point2Text') }
      ]
    },
    {
      title: t('hero.slide2Title'),
      description: '',
      image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/0d2e1176096a88a27e6664e862302f4a.png',
      icon: 'ri-shield-check-line',
      mainTitle: t('hero.slide2MainTitle'),
      points: [
        { label: t('hero.slide2Point1'), text: t('hero.slide2Point1Text') },
        { label: t('hero.slide2Point2'), text: t('hero.slide2Point2Text') },
        { label: t('hero.slide2Point3'), text: t('hero.slide2Point3Text') }
      ]
    },
    {
      title: t('hero.slide3Title'),
      description: '',
      image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/369fee89ca16963cf07151fecf8a96fd.png',
      icon: 'ri-flask-line',
      mainTitle: t('hero.slide3MainTitle'),
      points: [
        { label: t('hero.slide3Point1'), text: t('hero.slide3Point1Text') },
        { label: t('hero.slide3Point2'), text: t('hero.slide3Point2Text') },
        { label: t('hero.slide3Point3'), text: t('hero.slide3Point3Text') }
      ]
    },
    {
      title: t('hero.slide4Title'),
      description: '',
      image: 'https://readdy.ai/api/search-image?query=Sustainable%20eco-friendly%20environmental%20certification%20concept%20with%20green%20leaf%20and%20soil%20representing%20carbon%20footprint%20reduction%20renewable%20energy%20solar%20panels%20wind%20turbines%20beautiful%20earth%20globe%20sustainable%20future%20environmental%20responsibility%20corporate%20social%20responsibility%20green%20business%20practices%20clean%20energy%20transition&width=800&height=600&seq=eco-carbon-footprint-slide-v3-004&orientation=landscape',
      icon: 'ri-leaf-line',
      mainTitle: t('hero.slide4MainTitle'),
      points: [
        { label: t('hero.slide4Point1'), text: t('hero.slide4Point1Text') },
        { label: t('hero.slide4Point2'), text: t('hero.slide4Point2Text') },
        { label: t('hero.slide4Point3'), text: t('hero.slide4Point3Text') }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img
                  src="https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/b662d3a48c077d96bf60f8f34958dd8b.png"
                  alt="CTL Logo"
                  className="h-20 w-auto"
              />
          </div>

            {/* Navigation Links */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => scrollToSection('home')}
                className={`px-3 py-2 rounded-lg ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSection === 'home' ? 'bg-teal-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t('nav.home')}
              </button>
              
              {/* Business Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setShowBusinessMenu(true)}
                onMouseLeave={() => setShowBusinessMenu(false)}
              >
                <button
                  className={`px-3 py-2 rounded-lg ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                    activeSection === 'services' ? 'bg-teal-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('nav.business')}
                  <i className={`ri-arrow-down-s-line transition-transform ${showBusinessMenu ? 'rotate-180' : ''}`}></i>
                </button>
                {showBusinessMenu && (
                  <div
                    onMouseEnter={() => setShowBusinessMenu(true)}
                    className="absolute top-full left-0 pt-1 w-48 z-50"
                  >
                    <div className="bg-white rounded-lg shadow-lg py-2">
                    <button
                      onClick={() => {
                        navigate('/business/testing-analysis');
                        setShowBusinessMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.testingAnalysis')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/business/product-testing');
                        setShowBusinessMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.productTesting')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/business/certification');
                        setShowBusinessMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.certificationMark')}
                    </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Customer Support Dropdown */}
              <div 
                className="relative group"
                onMouseEnter={() => setShowCustomerSupportMenu(true)}
                onMouseLeave={() => setShowCustomerSupportMenu(false)}
              >
                <button 
                  className={`px-3 py-2 rounded-lg ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-1 text-gray-700 hover:bg-gray-100`}
                >
                  {t('nav.customerSupport')}
                  <i className={`ri-arrow-down-s-line transition-transform ${showCustomerSupportMenu ? 'rotate-180' : ''}`}></i>
                </button>
                {showCustomerSupportMenu && (
                  <div
                    onMouseEnter={() => setShowCustomerSupportMenu(true)}
                    className="absolute top-full left-0 pt-1 w-56 z-50"
                  >
                    <div className="bg-white rounded-lg shadow-lg py-2">
                    <button
                      onClick={() => {
                        navigate('/customer-support/industry-solutions');
                        setShowCustomerSupportMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.industrySolutions')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/customer-support/certification-support');
                        setShowCustomerSupportMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.certificationSupport')}
                    </button>
                    
                    {/* Technical Center with submenu */}
                    <div className="relative group/tech">
                      <button
                        className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center justify-between`}
                      >
                        {t('nav.technicalCenter')}
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                      <div className="absolute left-full top-0 ml-1 w-48 bg-white rounded-lg shadow-lg py-2 opacity-0 invisible group-hover/tech:opacity-100 group-hover/tech:visible transition-all">
                        <button
                          onClick={() => {
                            navigate('/customer-support/technical-center/jewelry');
                            setShowCustomerSupportMenu(false);
                          }}
                          className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                        >
                          {t('nav.jewelry')}
                        </button>
                        <button
                          onClick={() => {
                            navigate('/customer-support/technical-center/children-baby');
                            setShowCustomerSupportMenu(false);
                          }}
                          className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                        >
                          {t('nav.childrenBaby')}
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        navigate('/customer-support/education-center');
                        setShowCustomerSupportMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.educationCenter')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/customer-support/regulation-research');
                        setShowCustomerSupportMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.regulationResearch')}
                    </button>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => scrollToSection('equipment-section')}
                className={`px-3 py-2 rounded-lg ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSection === 'equipment' ? 'bg-teal-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t('nav.equipmentCertifications')}
              </button>
              
              <button
                onClick={() => {
                  navigate('/announcements');
                }}
                className={`px-3 py-2 rounded-lg ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} font-medium transition-all whitespace-nowrap cursor-pointer text-gray-700 hover:bg-gray-100`}
              >
                {t('nav.announcements')}
              </button>

              {/* About Dropdown - 언어선택 바로 왼쪽으로 이동 */}
              <div 
                className="relative"
                onMouseEnter={() => setShowAboutMenu(true)}
                onMouseLeave={() => setShowAboutMenu(false)}
              >
                <button
                  className={`px-3 py-2 rounded-lg ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                    ['about', 'business'].includes(activeSection) ? 'bg-teal-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('nav.about')}
                  <i className={`ri-arrow-down-s-line transition-transform ${showAboutMenu ? 'rotate-180' : ''}`}></i>
                </button>
                {showAboutMenu && (
                  <div
                    onMouseEnter={() => setShowAboutMenu(true)}
                    className="absolute top-full left-0 pt-1 w-48 z-50"
                  >
                    <div className="bg-white rounded-lg shadow-lg py-2">
                    <button
                      onClick={() => {
                        navigate('/about/ceo-message');
                        setShowAboutMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.ceoMessage')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/about/management-policy');
                        setShowAboutMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.managementPolicy')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/about/company-info');
                        setShowAboutMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.companyInfo')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/about/company-history');
                        setShowAboutMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.companyHistory')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/about/certifications');
                        setShowAboutMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.certifications')}
                    </button>
                    <button
                      onClick={() => {
                        navigate('/about/organization');
                        setShowAboutMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} text-gray-700 hover:bg-gray-100 cursor-pointer`}
                    >
                      {t('nav.organization')}
                    </button>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => scrollToSection('location')}
                className={`px-3 py-2 rounded-lg ${i18n.language === 'en' ? 'text-sm' : 'text-lg'} font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSection === 'location' ? 'bg-teal-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t('nav.location')}
              </button>

              {/* Language Selector */}
              <div className="relative ml-2">
                <button
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  onBlur={() => setTimeout(() => setShowLangMenu(false), 200)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all cursor-pointer"
                >
                  <img 
                    src={currentLanguage.flag} 
                    alt={currentLanguage.name}
                    className="w-5 h-4 object-cover rounded"
                  />
                  <span>{currentLanguage.code.toUpperCase()}</span>
                  <i className={`ri-arrow-down-s-line transition-transform ${showLangMenu ? 'rotate-180' : ''}`}></i>
                </button>
                {showLangMenu && (
                  <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-lg shadow-lg py-2 z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                      >
                        <img 
                          src={lang.flag} 
                          alt={lang.name}
                          className="w-5 h-4 object-cover rounded"
                        />
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Unified Slide */}
      <section id="home" className="relative flex items-center justify-center overflow-hidden bg-white" style={{ height: '60vh', minHeight: '500px', marginTop: '80px' }}>
        <div className="relative w-full h-full max-w-7xl mx-auto px-6">
          {/* Slide Container */}
          <div className="relative w-full h-full overflow-hidden">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-1000 ${
                  index === currentSlide 
                    ? 'opacity-100 translate-x-0' 
                    : index === (currentSlide - 1 + slides.length) % slides.length
                    ? 'opacity-0 -translate-x-full'
                    : 'opacity-0 translate-x-full'
                }`}
              >
                <div className="flex items-center h-full gap-12">
                  {/* Left Side - Text Content */}
                  <div className="w-1/2 space-y-6">
                    <div className="inline-block px-4 py-1 bg-teal-600/90 rounded-full">
                      <p className="text-white text-sm font-bold whitespace-nowrap">{t('hero.badge')}</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${slide.icon} text-xl text-white`}></i>
                        </div>
                        <h2 className="text-3xl font-black text-gray-900">
                          {slide.mainTitle}
                        </h2>
                      </div>
                      <div className="ml-13 space-y-2">
                        {slide.points.map((point, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-fill text-teal-600 text-base mt-1"></i>
                            <p className="text-base text-gray-700 leading-relaxed">
                              {point.text && point.text.includes(point.label) ? (
                                <>
                                  {point.text.split(point.label)[0]}
                                  <strong className="text-gray-900">{point.label}</strong>
                                  {point.text.split(point.label)[1]}
                                </>
                              ) : (
                                <>
                                  <strong className="text-gray-900">{point.label}</strong>
                                  {point.text}
                                </>
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start gap-4 pt-4">
                      <button 
                        onClick={() => scrollToSection('services')}
                        className="px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold text-base hover:bg-teal-700 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl whitespace-nowrap cursor-pointer"
                      >
                        {t('hero.btnServices')}
                      </button>
                      <button 
                        onClick={() => scrollToSection('contact')}
                        className="px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold text-base hover:bg-gray-100 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl whitespace-nowrap cursor-pointer border-2 border-gray-300"
                      >
                        {t('hero.btnContact')}
                      </button>
                    </div>
                  </div>

                  {/* Right Side - Image */}
                  <div className="w-1/2 h-full">
                    <div className="relative h-full rounded-2xl overflow-hidden shadow-2xl">
                      <img
                        src={slide.image}
                        alt={slide.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center space-x-3 z-20">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`transition-all cursor-pointer ${
                  index === currentSlide
                    ? 'w-12 h-3 bg-teal-600'
                    : 'w-3 h-3 bg-gray-400 hover:bg-gray-600'
                } rounded-full`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Spacing between sections */}
      <div className="h-8 bg-white"></div>

      {/* Product Slider Section - 40% height */}
      <section className="relative bg-white overflow-hidden" style={{ height: '32vh', minHeight: '200px' }}>
        <div className="absolute inset-0 flex items-center">
          <div 
            className="flex transition-transform duration-1000 ease-in-out"
            style={{
              transform: `translateX(-${(productSlideOffset * 100) / 6}%)`,
              width: `${(productImages.length * 100) / 6}%`
            }}
          >
            {[...productImages, ...productImages].map((product, index) => (
              <div
                key={index}
                className="flex-shrink-0 px-4"
                style={{ width: `${100 / productImages.length}%` }}
              >
                <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full hover:shadow-xl transition-all group">
                  <div className="relative h-40 w-full overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 text-center">
                    <h3 className={`text-lg font-bold text-gray-900 ${i18n.language === 'en' ? '' : 'whitespace-nowrap overflow-hidden text-ellipsis'}`}>{product.name}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ISO 17025 Certified Cards - 아래로 이동 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: t('hero.card1Title'), desc: t('hero.card1Desc'), delay: '0ms' },
              { icon: 'ri-global-line', title: t('hero.card2Title'), desc: t('hero.card2Desc'), delay: '100ms' },
              { icon: 'ri-microscope-line', title: t('hero.card3Title'), desc: t('hero.card3Desc'), delay: '200ms' },
              { icon: 'ri-team-line', title: t('hero.card4Title'), desc: t('hero.card4Desc'), delay: '300ms' }
            ].map((card, index) => (
              <div 
                key={index}
                className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 animate-slide-up border border-gray-200"
                style={{ animationDelay: card.delay }}
              >
                <div className="w-14 h-14 bg-teal-600 rounded-lg flex items-center justify-center mb-4 animate-float" style={{ animationDelay: card.delay }}>
                  <i className={`${card.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-gray-600 text-sm">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-4">
              <span className="text-teal-700 font-semibold text-sm whitespace-nowrap">{t('about.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('about.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-start space-x-6">
                <div className="w-20 h-20 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-target-line text-4xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('about.mission')}</h3>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {t('about.missionDesc')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-start space-x-6">
                <div className="w-20 h-20 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-eye-line text-4xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('about.vision')}</h3>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {t('about.visionDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-10 mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">{t('about.values')}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-focus-line', title: t('about.value1'), desc: t('about.value1Desc'), color: 'from-blue-500 to-blue-600' },
              { icon: 'ri-medal-line', title: t('about.value2'), desc: t('about.value2Desc'), color: 'from-purple-500 to-purple-600' },
              { icon: 'ri-rocket-line', title: t('about.value3'), desc: t('about.value3Desc'), color: 'from-orange-500 to-orange-600' },
              { icon: 'ri-heart-line', title: t('about.value4'), desc: t('about.value4Desc'), color: 'from-pink-500 to-pink-600' }
            ].map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all group">
                <div className={`w-14 h-14 bg-gradient-to-br ${value.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <i className={`${value.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - 综合检测解决方案（内容与图片一致） */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-4">
              <span className="text-teal-700 font-semibold text-sm whitespace-nowrap">{t('services.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('services.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-flask-line', titleKey: 'service1Title', itemKeys: ['service1Item1', 'service1Item2', 'service1Item3', 'service1Item4', 'service1Item5'] },
              { icon: 'ri-parent-line', titleKey: 'service2Title', itemKeys: ['service2Item1', 'service2Item2', 'service2Item3', 'service2Item4', 'service2Item5'] },
              { icon: 'ri-vip-diamond-line', titleKey: 'service3Title', itemKeys: ['service3Item1', 'service3Item2', 'service3Item3', 'service3Item4', 'service3Item5'] },
              { icon: 'ri-file-text-line', titleKey: 'service4Title', itemKeys: ['service4Item1', 'service4Item2', 'service4Item3', 'service4Item4', 'service4Item5'] },
              { icon: 'ri-search-line', titleKey: 'service5Title', itemKeys: ['service5Item1', 'service5Item2', 'service5Item3'] },
              { icon: 'ri-customer-service-2-line', titleKey: 'service6Title', itemKeys: ['service6Item1', 'service6Item2', 'service6Item3'] }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all group">
                <div className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{t(`services.${service.titleKey}`)}</h3>
                  <ul className="space-y-2">
                    {service.itemKeys.map((key) => (
                      <li key={key} className="flex items-center text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600 mr-2 flex-shrink-0"></i>
                        <span>{t(`services.${key}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spacing between sections */}
      <div className="h-24 bg-white"></div>

      {/* Certifications Section */}
      <section id="certifications" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-4">
              <span className="text-teal-700 font-semibold text-sm whitespace-nowrap">{t('certifications.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('certifications.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('certifications.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <Link to="/certifications/iso" className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-all cursor-pointer block">
              <div className="flex items-start space-x-6">
                <div className="w-20 h-20 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-award-line text-4xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-3">{t('certifications.cert1Title')}</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {t('certifications.cert1Desc')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-teal-700 whitespace-nowrap">{t('certifications.cert1Tag1')}</span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-teal-700 whitespace-nowrap">{t('certifications.cert1Tag2')}</span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-teal-700 whitespace-nowrap">{t('certifications.cert1Tag3')}</span>
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/certifications/ilac" className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-all cursor-pointer block">
              <div className="flex items-start space-x-6">
                <div className="w-20 h-20 bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-4xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-3">{t('certifications.cert2Title')}</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {t('certifications.cert2Desc')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-gray-700 whitespace-nowrap">{t('certifications.cert2Tag1')}</span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-gray-700 whitespace-nowrap">{t('certifications.cert2Tag2')}</span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-gray-700 whitespace-nowrap">{t('certifications.cert2Tag3')}</span>
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/certifications/cpsc" className="bg-gradient-to-br from-sky-50 to-blue-100 rounded-2xl p-10 shadow-lg border-2 border-sky-200 hover:shadow-xl transition-all cursor-pointer block">
              <div className="flex items-start space-x-6">
                <div className="w-20 h-20 bg-gradient-to-br from-sky-500 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                  <i className="ri-shield-check-line text-4xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-sky-700 mb-3">{t('certifications.cert3Title')}</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {t('certifications.cert3Desc')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-white border border-sky-300 rounded-full text-sm font-medium text-sky-700 whitespace-nowrap shadow-sm">{t('certifications.cert3Tag1')}</span>
                    <span className="px-3 py-1 bg-white border border-sky-300 rounded-full text-sm font-medium text-sky-700 whitespace-nowrap shadow-sm">{t('certifications.cert3Tag2')}</span>
                    <span className="px-3 py-1 bg-white border border-sky-300 rounded-full text-sm font-medium text-sky-700 whitespace-nowrap shadow-sm">{t('certifications.cert3Tag3')}</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10">
            {[
              { region: t('certifications.region1'), standards: ['REACH', 'RoHS', 'EN 71', 'Phthalates'], icon: 'ri-earth-line', link: null },
              { region: t('certifications.region2'), standards: ['CPSIA', 'ASTM F963', 'California Prop 65', 'CPSC'], icon: 'ri-flag-line', link: null },
              { region: t('certifications.region3'), standards: ['GB Standards', 'JIS', 'KS', 'CNS'], icon: 'ri-map-pin-line', link: null },
              { region: t('certifications.region4'), standards: ['ISO', 'IEC', 'ILAC MRA', 'OECD GLP'], icon: 'ri-global-line', link: '/international-standards' }
            ].map((region, index) => (
              region.link ? (
                <Link
                  key={index}
                  to={region.link}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${region.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center justify-between">
                    {region.region}
                    <i className="ri-arrow-right-line text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                  </h3>
                  <ul className="space-y-1">
                    {region.standards.map((standard, idx) => (
                      <li key={idx} className="text-lg text-gray-600">• {standard}</li>
                    ))}
                  </ul>
                </Link>
              ) : (
                <div 
                  key={index} 
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 transition-all"
                >
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${region.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-3">{region.region}</h4>
                  <ul className="space-y-1">
                    {region.standards.map((standard, idx) => (
                      <li key={idx} className="text-sm text-gray-600">• {standard}</li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      <section id="equipment-section" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('equipment.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('equipment.subtitle')}
            </p>
          </div>

          <div className="relative">
            {/* Navigation Buttons */}
            <button
              onClick={() => goToEquipmentSlide('prev')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <button
              onClick={() => goToEquipmentSlide('next')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-teal-600 hover:text-white transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>

            {/* Slider Container */}
            <div className="overflow-hidden px-16">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${(equipmentSlideOffset * 100) / 3}%)`
                }}
              >
                {[
                  {
                    name: t('hero.equipment1Name'),
                    fullName: 'Agilent Gas Chromatography-Mass Spectrometry',
                    description: t('hero.equipment1Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/c67c39aa80146fbef5fac861e73ffb34.jpeg',
                    applications: [t('hero.equipment1App1'), t('hero.equipment1App2'), t('hero.equipment1App3')]
                  },
                  {
                    name: t('hero.equipment2Name'),
                    fullName: 'Equipment 2 - Full Name',
                    description: t('hero.equipment2Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/62a24c6dc8b7016fa4b192f358880907.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment3Name'),
                    fullName: 'Rayleigh UV-1801 Spectrophotometer',
                    description: t('hero.equipment3Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/2f28d600a2419474715076420ad852bf.jpeg',
                    applications: [t('hero.equipment3App1'), t('hero.equipment3App2'), t('hero.equipment3App3')]
                  },
                  {
                    name: t('hero.equipment4Name'),
                    fullName: 'Equipment 4 - Full Name',
                    description: t('hero.equipment4Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/733e9577fae514040cfc0bc0ebc46972.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment5Name'),
                    fullName: 'Equipment 5 - Full Name',
                    description: t('hero.equipment5Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/dc0bccc02b4f2e224efac359f06ab71d.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment6Name'),
                    fullName: 'Equipment 6 - Full Name',
                    description: t('hero.equipment6Desc'),
                    image: 'https://readdy.ai/api/search-image?query=Modern%20laboratory%20testing%20equipment%20number%206%2C%20professional%20scientific%20instrument%20with%20digital%20display%2C%20clean%20white%20background%2C%20high-tech%20analytical%20device&width=600&height=400&seq=equip-006&orientation=landscape',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment7Name'),
                    fullName: 'ETHOS EASY - Advanced Microwave Digestion System',
                    description: t('hero.equipment7Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/fbf2b1078e6f7473d4cf47486367c30e.jpeg',
                    applications: [t('hero.equipment7App1'), t('hero.equipment7App2'), t('hero.equipment7App3')]
                  },
                  {
                    name: t('hero.equipment8Name'),
                    fullName: 'Muffle Furnace - High Temperature Laboratory Furnace',
                    description: t('hero.equipment8Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/a89df2eeb794d8406d2b0e25d98ef1bc.jpeg',
                    applications: [t('hero.equipment8App1'), t('hero.equipment8App2'), t('hero.equipment8App3')]
                  },
                  {
                    name: t('hero.equipment9Name'),
                    fullName: 'Equipment 9 - Full Name',
                    description: t('hero.equipment9Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/1498c037929a081f9a533e258726d74b.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment10Name'),
                    fullName: 'Equipment 10 - Full Name',
                    description: t('hero.equipment10Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/26dc5b169a6450c97196dfa207f93062.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment11Name'),
                    fullName: 'Equipment 11 - Full Name',
                    description: t('hero.equipment11Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/cf389f3cb3c7decd4eb32498f2614fcc.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment12Name'),
                    fullName: 'Equipment 12 - Full Name',
                    description: t('hero.equipment12Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/109ffb5151a14b926b66998261d30e14.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment13Name'),
                    fullName: 'Equipment 13 - Full Name',
                    description: t('hero.equipment13Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/7dcfe47e04a2bd97d655c0c5a28811c3.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment14Name'),
                    fullName: 'Equipment 14 - Full Name',
                    description: t('hero.equipment14Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/ab367b936cad01dfab64210e62631e3c.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment15Name'),
                    fullName: 'COX EM-30AX - Advanced Analytical System',
                    description: t('hero.equipment15Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/bf5780e033e77fb323f9ab8e65856026.jpeg',
                    applications: [t('hero.equipment15App1'), t('hero.equipment15App2'), t('hero.equipment15App3')]
                  },
                  {
                    name: t('hero.equipment16Name'),
                    fullName: 'Equipment 16 - Full Name',
                    description: t('hero.equipment16Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/cc3ba652a41394890f74ad155f09ff3c.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment17Name'),
                    fullName: 'Equipment 17 - Full Name',
                    description: t('hero.equipment17Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/f6f3cba9f6d3e423250cdeb08195f71e.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment18Name'),
                    fullName: 'Vision Inspection System',
                    description: t('hero.equipment18Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/d59c02f744b1105900b763449223bf7b.jpeg',
                    applications: [t('hero.equipment18App1'), t('hero.equipment18App2'), t('hero.equipment18App3')]
                  },
                  {
                    name: t('hero.equipment19Name'),
                    fullName: 'X-ray Fluorescence Analyzer (XRF)',
                    description: t('hero.equipment19Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/2f007f4379e88b3e7d8560c644982072.jpeg',
                    applications: [t('hero.equipment19App1'), t('hero.equipment19App2'), t('hero.equipment19App3')]
                  },
                  {
                    name: t('hero.equipment20Name'),
                    fullName: 'Thermo Scientific Analytical System',
                    description: t('hero.equipment20Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/45a0053b50a3a57334da6b3f04b43ad2.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  },
                  {
                    name: t('hero.equipment21Name'),
                    fullName: 'HPLC System - High Performance Liquid Chromatography',
                    description: t('hero.equipment21Desc'),
                    image: 'https://static.readdy.ai/image/5010d926a533fffa2c697af9ffdcdc7c/9ccfcffaf19cd50a26164328752bb7e4.jpeg',
                    applications: [t('hero.equipment2App1'), t('hero.equipment2App2'), t('hero.equipment2App3')]
                  }
                ].map((equipment, index) => (
                  <div key={index} className="flex-shrink-0 px-4" style={{ width: '33.333%' }}>
                    <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all group">
                      <div className="relative h-56 w-full overflow-hidden">
                        <img 
                          src={equipment.image}
                          alt={equipment.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">{equipment.name}</h3>
                        <p className="text-xs text-teal-600 font-medium mb-3">{equipment.fullName}</p>
                        <p className="text-gray-600 mb-4 text-sm leading-relaxed">{equipment.description}</p>
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-700 mb-2">{t('equipment.applicationsLabel')}</p>
                          {equipment.applications.map((app, idx) => (
                            <div key={idx} className="flex items-center text-sm text-gray-600">
                              <i className="ri-checkbox-circle-fill text-teal-600 mr-2"></i>
                              <span>{app}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Slide Indicators */}
            <div className="flex justify-center items-center space-x-2 mt-8">
              {Array.from({ length: 21 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setEquipmentSlideOffset(index)}
                  className={`transition-all cursor-pointer ${
                    index === equipmentSlideOffset
                      ? 'w-8 h-2 bg-teal-600'
                      : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                  } rounded-full`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-4">
              <span className="text-teal-700 font-semibold text-sm whitespace-nowrap">{t('contact.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('contact.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.infoTitle')}</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">{t('contact.phone')}</p>
                      <p className="text-gray-600">0532-55566163/55566165</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">{t('contact.email')}</p>
                      <p className="text-gray-600">ctl@centrallab.biz</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">{t('contact.address')}</p>
                      <p className="text-gray-600">{t('contact.addressLine2')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">{t('contact.hours')}</p>
                      <p className="text-gray-600">{t('contact.hoursWeekday')}</p>
                      <p className="text-gray-600">{t('contact.hoursWeekend')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 text-white shadow-lg hover:shadow-xl transition-all mt-8">
                <h3 className="text-2xl font-bold mb-4">{t('contact.quickTitle')}</h3>
                <p className="text-white/90 mb-6">
                  {t('contact.quickDesc')}
                </p>
                <div className="flex items-center space-x-3">
                  <i className="ri-customer-service-2-line text-3xl"></i>
                  <div>
                    <p className="font-semibold">{t('contact.quickPhone')}</p>
                    <p className="text-xl font-bold">0532-55566163/55566165</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.formTitle')}</h3>
              <form 
                className="space-y-6" 
                data-readdy-form 
                id="contact-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const data = new URLSearchParams();
                  
                  formData.forEach((value, key) => {
                    data.append(key, value.toString());
                  });

                  try {
                    const response = await fetch('https://readdy.ai/api/form/d535jqjontabvievtoi0', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: data.toString()
                    });

                    if (response.ok) {
                      alert(t('contact.formSuccess'));
                      form.reset();
                    } else {
                      alert(t('contact.formError'));
                    }
                  } catch (error) {
                    alert(t('contact.formError'));
                  }
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.formName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder={t('contact.formName')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.formCompany')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder={t('contact.formCompany')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.formEmail')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.formPhone')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="010-1234-5678"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.formService')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="service"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">{t('contact.formServicePlaceholder')}</option>
                    <option value={t('contact.formServiceOption1')}>{t('contact.formServiceOption1')}</option>
                    <option value={t('contact.formServiceOption2')}>{t('contact.formServiceOption2')}</option>
                    <option value={t('contact.formServiceOption3')}>{t('contact.formServiceOption3')}</option>
                    <option value={t('contact.formServiceOption4')}>{t('contact.formServiceOption4')}</option>
                    <option value={t('contact.formServiceOption5')}>{t('contact.formServiceOption5')}</option>
                    <option value={t('contact.formServiceOption6')}>{t('contact.formServiceOption6')}</option>
                    <option value={t('contact.formServiceOption7')}>{t('contact.formServiceOption7')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.formMessage')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder={t('contact.formMessagePlaceholder')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold text-lg hover:bg-teal-700 hover:scale-105 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  {t('contact.formSubmit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 rounded-full mb-4">
              <span className="text-teal-700 font-semibold text-sm whitespace-nowrap">{t('location.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('location.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('location.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Side - Transportation Info */}
            <div className="space-y-6">
              <div 
                className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => setShowSubwayMap(true)}
              >
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-subway-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center justify-between">
                  {t('location.subway')}
                  <i className="ri-arrow-right-line text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </h3>
                <p className="text-gray-700 mb-3 font-semibold">{t('location.subwayLine')}</p>
                <p className="text-sm text-gray-600">{t('location.subwayDesc')}</p>
                <div className="mt-4 inline-flex items-center space-x-2 text-teal-600 font-medium text-sm">
                  <i className="ri-map-2-line"></i>
                  <span>{t('location.subwayMapLink')}</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-car-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('location.car')}</h3>
                {t('location.carParking') && <p className="text-gray-700 mb-3 font-semibold">{t('location.carParking')}</p>}
                {t('location.carDesc') && <p className="text-sm text-gray-600">{t('location.carDesc')}</p>}
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-bus-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('location.bus')}</h3>
                <p className="text-gray-700 mb-3 font-semibold">{t('location.busLines')}</p>
                <p className="text-sm text-gray-600">{t('location.busDesc')}</p>
              </div>
            </div>

            {/* Right Side - Baidu Map */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
              <div className="relative w-full h-[600px]">
                <iframe
                  src="https://map.baidu.com/search/%E9%9D%92%E5%B2%9B%E5%B8%82%E5%9F%8E%E9%98%B3%E5%8C%BA/@13326825.17,4000825.5,12z?querytype=s&da_src=shareurl&wd=%E9%9D%92%E5%B2%9B%E5%B8%82%E5%9F%8E%E9%98%B3%E5%8C%BA&c=224&src=0&pn=0&sug=0&l=13&b=(13298201,3987313;13355449,4014337)&from=webmap&biz_forward=%7B%32scaler%82:1,%%22styles%82:%%22pl%82%7D&device_ratio=1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Laboratory Location Map - Baidu"
                ></iframe>
              </div>
              <div className="p-8 bg-gray-50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{t('contact.address')}</h4>
                    <p className="text-gray-700 mb-1">{t('contact.addressLine1')}</p>
                    <p className="text-gray-700">{t('contact.addressLine2')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/61ea8732-385a-4541-8aab-5452a62776b3.png" 
                  alt="Logo" 
                  className="h-10 w-auto"
                />
                <div>
                  <h3 className="text-lg font-bold">Testing Laboratory</h3>
                  <p className="text-xs text-gray-400">ISO 17025 Certified</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                {t('footer.description')}
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">{t('footer.servicesTitle')}</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.service1')}</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.service2')}</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.service3')}</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer.service4')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">{t('footer.quickLinksTitle')}</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('nav.home')}</button></li>
                <li><button onClick={() => scrollToSection('certifications')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('nav.certifications')}</button></li>
                <li><button onClick={() => scrollToSection('equipment')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('nav.equipment')}</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('nav.contact')}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">{t('footer.contactTitle')}</h4>
              <ul className="space-y-3">
                <li className="flex items-center space-x-2 text-sm text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>0532-55566163/55566165</span>
                </li>
                <li className="flex items-center space-x-2 text-sm text-gray-400">
                  <i className="ri-mail-line"></i>
                  <span>ctl@centrallab.biz</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-gray-400">
                  <i className="ri-map-pin-line mt-1"></i>
                  <span>{t('contact.addressLine1')}<br />{t('contact.addressLine2')}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                {t('footer.copyright')}
              </p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
              >
                {t('footer.poweredBy')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}